import axios from 'axios';
import { msalInstance } from '../main';
import { apiScopes } from '../auth/authConfig';

const api = axios.create({
    baseURL: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 120000, // 60 seconds default timeout
});

// Request interceptor to add backend API token
api.interceptors.request.use(
    async (config) => {
        const account = msalInstance.getActiveAccount();
        if (account) {
            try {
                const response = await msalInstance.acquireTokenSilent({
                    scopes: apiScopes.backend,
                    account: account,
                });
                config.headers.Authorization = `Bearer ${response.accessToken}`;
            } catch (error: any) {
                console.error('Backend token acquisition failed:', error);
                
                // Handle iframe timeout errors gracefully
                if (error.errorCode === 'monitor_window_timeout' || error.message?.includes('timeout')) {
                    console.warn('Token acquisition timed out, attempting fallback to redirect...');
                    
                    // Try one more time with a fresh attempt
                    try {
                        const retryResponse = await msalInstance.acquireTokenSilent({
                            scopes: apiScopes.backend,
                            account: account,
                            forceRefresh: true
                        });
                        config.headers.Authorization = `Bearer ${retryResponse.accessToken}`;
                        return config;
                    } catch (retryError: any) {
                        // If retry fails, redirect to login
                        console.error('Token acquisition retry failed:', retryError);
                        await msalInstance.loginRedirect({
                            scopes: apiScopes.graph,
                            prompt: 'select_account'
                        });
                        throw new Error('Token acquisition timed out, redirecting to login');
                    }
                }
                
                if (error.name === 'InteractionRequiredAuthError') {
                    await msalInstance.loginRedirect({
                        scopes: apiScopes.graph,
                        prompt: 'select_account'
                    });
                    throw new Error('Token expired, redirecting to login');
                }
                throw error;
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        if (error.response?.status === 401) {
            console.error('Backend API unauthorized');
            const account = msalInstance.getActiveAccount();
            if (account) {
                try {
                    // Try to refresh token
                    await msalInstance.acquireTokenSilent({
                        scopes: apiScopes.backend,
                        account: account,
                        forceRefresh: true
                    });
                    // Retry the request
                    return api.request(error.config);
                } catch (tokenError) {
                    console.error('Token refresh failed:', tokenError);
                    await msalInstance.loginRedirect({
                        scopes: apiScopes.graph,
                        prompt: 'select_account'
                    });
                }
            }
        }
        return Promise.reject(error);
    }
);

export default api;